// Admin Campaigns — Messaging v2 (/api/admin/v2/messaging) + per-user comms

export type MessagingChannel = 'in_app' | 'email' | 'sms' | 'whatsapp';

export type ChannelDeliveryStatus =
  | 'sent'
  | 'failed'
  | 'opted_out'
  | 'suppressed'
  | 'no_address'
  | 'skipped'
  | string;

export type SuppressionScope = 'sms' | 'voice' | 'all';

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  has_more?: boolean;
}

export interface RecipientRef {
  uid?: number;
  oid?: string | number;
  email?: string;
  phone?: string;
}

export interface ChannelOutcome {
  status: ChannelDeliveryStatus;
  unid?: number;
  error?: string;
  [key: string]: unknown;
}

export interface SendRecipientResult {
  uid?: number | null;
  ref?: RecipientRef;
  channels?: Partial<Record<MessagingChannel, ChannelOutcome>>;
  [key: string]: unknown;
}

export interface SendTally {
  sent?: number;
  failed?: number;
  opted_out?: number;
  suppressed?: number;
  no_address?: number;
  skipped?: number;
  [key: string]: unknown;
}

export interface SendResult {
  success?: boolean;
  msgid?: string;
  trace_id?: string;
  channels?: MessagingChannel[];
  name_system?: string;
  override_applied?: boolean;
  override_denied?: boolean;
  recipients_total?: number;
  tally?: SendTally;
  recipients?: SendRecipientResult[];
  [key: string]: unknown;
}

export interface SendPayload {
  recipients?: RecipientRef[];
  uids?: number[];
  channels: MessagingChannel[];
  subject?: string;
  body: string;
  html?: string;
  respect_prefs?: boolean;
  reason?: string;
  /** Email only: the mailbox to send from (see `useMailboxesQuery`). */
  from?: string;
  /** SMS only: the line to send from (see `useSmsLinesQuery`); omitted = Hubtel. */
  sms_line?: string;
  /** Email only: add the mailbox's signature (default true). */
  signature?: boolean;
  /** Email only: files as base64, at most 10 and 10 MB in all. */
  attachments?: EmailAttachment[];
}

export type ConsentStatus = 'ok' | 'opted_out' | 'suppressed' | 'no_address' | 'skipped';

/** POST /send/check: what a send would do for each recipient, without sending. */
export interface SendCheckResult {
  recipients: { ref: RecipientRef; uid?: number | null; name?: string | null; email?: string | null; channels: Partial<Record<MessagingChannel, ConsentStatus>> }[];
  can_override: boolean;
}

export interface EmailAttachment {
  name: string;
  /** base64, no data: prefix */
  content: string;
}

export interface AdminSendRecord {
  msgid: string;
  actor_uid?: number;
  channels?: MessagingChannel[] | string;
  subject?: string | null;
  body?: string | null;
  name_system?: string;
  recipients_total?: number;
  tally?: SendTally;
  created_at?: number | string;
  reason?: string | null;
  override_applied?: boolean;
  [key: string]: unknown;
}

export interface SendsListResponse {
  sends?: AdminSendRecord[];
  pagination?: Pagination;
  [key: string]: unknown;
}

export interface SendListParams {
  actor_uid?: number;
  channel?: MessagingChannel | string;
  limit?: number;
  offset?: number;
}

export interface NotificationTemplate {
  ntmplid: number;
  name_system: string;
  name?: string | null;
  locale?: string;
  email_html?: string | null;
  email_text?: string | null;
  email_params?: string[];
  sms_text?: string | null;
  sms_params?: string[];
  brevo_template_id?: string | null;
  ses_template_id?: string | null;
  [key: string]: unknown;
}

export interface TemplatesListResponse {
  templates?: NotificationTemplate[];
  pagination?: Pagination;
  [key: string]: unknown;
}

export interface TemplateListParams {
  name_system?: string;
  locale?: string;
  limit?: number;
  offset?: number;
}

export interface CreateTemplatePayload {
  name_system: string;
  name?: string;
  locale?: string;
  email_html?: string;
  email_text?: string;
  email_params?: string[];
  sms_text?: string;
  sms_params?: string[];
  brevo_template_id?: string;
  ses_template_id?: string;
}

export interface UpdateTemplatePayload {
  name?: string | null;
  email_html?: string | null;
  email_text?: string | null;
  email_params?: string[];
  sms_text?: string | null;
  sms_params?: string[];
  brevo_template_id?: string | null;
  ses_template_id?: string | null;
}

export interface PhoneSuppression {
  psid: number;
  phone: string;
  scope?: SuppressionScope | string;
  reason?: string | null;
  notes?: string | null;
  active?: number | boolean;
  created_at?: number | string;
  [key: string]: unknown;
}

export interface SuppressionsListResponse {
  suppressions?: PhoneSuppression[];
  pagination?: Pagination;
  [key: string]: unknown;
}

export interface SuppressionListParams {
  phone?: string;
  scope?: SuppressionScope | string;
  active_only?: boolean | 0 | 1 | string;
  limit?: number;
  offset?: number;
}

export interface AddSuppressionPayload {
  phone: string;
  scope?: SuppressionScope;
  reason?: string;
  notes?: string;
}

export interface NotificationType {
  ntypid: number;
  name_system?: string;
  name?: string | null;
  category?: string | null;
  active?: number | boolean;
  allow_in_app?: number | boolean;
  allow_email?: number | boolean;
  allow_sms?: number | boolean;
  default_in_app?: number | boolean;
  default_email?: number | boolean;
  default_sms?: number | boolean;
  [key: string]: unknown;
}

export interface NotificationTypesListResponse {
  notification_types?: NotificationType[];
  types?: NotificationType[];
  pagination?: Pagination;
  [key: string]: unknown;
}

export interface NotificationTypeListParams {
  category?: string;
  active_only?: boolean | 0 | 1 | string;
  limit?: number;
  offset?: number;
}

export interface UpdateNotificationTypePayload {
  active?: boolean;
  allow_in_app?: boolean;
  allow_email?: boolean;
  allow_sms?: boolean;
  default_in_app?: boolean;
  default_email?: boolean;
  default_sms?: boolean;
}

export interface CommsProfile {
  uid: number;
  authid?: string;
  name?: string | null;
  email1?: string | null;
  email1_verified?: boolean;
  email2?: string | null;
  phone1?: string | null;
  phone1_verified?: boolean;
  phone2?: string | null;
  country_iso2?: string | null;
  locale?: string | null;
  timezone?: string | null;
  admin_hold?: boolean;
  tester?: boolean;
  sms_suppressed?: boolean;
  whatsapp_opted_out?: boolean;
  [key: string]: unknown;
}

export interface CommsProfileResponse {
  comms_profile: CommsProfile;
}

export interface UserNotificationPreference {
  unpid?: number;
  ntypid: number;
  name_system?: string;
  name?: string | null;
  channel_email?: boolean | null;
  channel_sms?: boolean | null;
  channel_in_app?: boolean | null;
  [key: string]: unknown;
}

export interface UserPreferencesResponse {
  preferences?: UserNotificationPreference[];
  [key: string]: unknown;
}

export interface SetPreferencePayload {
  ntypid: number;
  channel_email?: boolean | null;
  channel_sms?: boolean | null;
  channel_in_app?: boolean | null;
}

export interface DeliveryHistoryEntry {
  channel?: string;
  status?: string;
  subject?: string | null;
  created_at?: number | string;
  msgid?: string;
  [key: string]: unknown;
}

export interface DeliveryHistoryResponse {
  history?: DeliveryHistoryEntry[];
  deliveries?: DeliveryHistoryEntry[];
  pagination?: Pagination;
  [key: string]: unknown;
}

export interface DeliveryHistoryParams {
  limit?: number;
  offset?: number;
}

export const CHANNEL_LABEL: Record<string, string> = {
  in_app: 'In-app',
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
};

export const DELIVERY_STATUS_BADGE: Record<string, string> = {
  sent: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-red-50 text-red-700',
  opted_out: 'bg-amber-50 text-amber-800',
  suppressed: 'bg-orange-50 text-orange-800',
  no_address: 'bg-gray-100 text-gray-600',
  skipped: 'bg-slate-100 text-slate-600',
};

export const formatEpoch = (ts?: number | string | null): string => {
  if (ts == null || ts === '') return '—';
  const n = typeof ts === 'string' ? Number(ts) : ts;
  if (!Number.isFinite(n)) {
    try {
      return new Date(String(ts)).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return String(ts);
    }
  }
  try {
    const ms = n > 1e12 ? n : n * 1000;
    return new Date(ms).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(ts);
  }
};

export const isTruthyFlag = (v: number | boolean | undefined | null) =>
  v === 1 || v === true;

export * from './lifecycle';
